/**
 * Design Gallery Integration for Hat Lab
 * Handles design selection, localStorage management, and UI updates
 * 100% backwards compatible - all functions check for element existence
 */

(function() {
  'use strict';

  // Create namespace to avoid conflicts
  window.HATLAB_DESIGN = window.HATLAB_DESIGN || {};

  /**
   * LocalStorage key for selected design
   * Separate from hatlab_settings to avoid conflicts
   */
  const STORAGE_KEY = 'hatlab_selected_design';

  /**
   * Get currently selected design from localStorage
   * @returns {Object|null} Design data or null if none selected
   */
  HATLAB_DESIGN.getSelectedDesign = function() {
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      return data ? JSON.parse(data) : null;
    } catch (e) {
      console.warn('Error reading selected design:', e);
      return null;
    }
  };

  /**
   * Save design selection to localStorage
   * @param {Object} designData - Design object with id, title, displayImage, etc.
   */
  HATLAB_DESIGN.selectDesign = function(designData) {
    try {
      const data = {
        id: designData.id,
        title: designData.title,
        description: designData.description || '',
        displayImage: designData.displayImage || designData.image, // For UI display
        thumbnail: designData.thumbnail || designData.displayImage || designData.image,
        previewImage: designData.previewImage || designData.image, // For future live preview
        selectedAt: Date.now(),
        selectedFrom: designData.selectedFrom || 'gallery'
      };
      
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      HATLAB_DESIGN.updateSelectedDesignBox();
      
      // Trigger custom event for other scripts to listen to
      document.dispatchEvent(new CustomEvent('hatlabDesignSelected', { detail: data }));
      
      return true;
    } catch (e) {
      console.warn('Could not save design selection:', e);
      return false;
    }
  };

  /**
   * Clear selected design from localStorage
   */
  HATLAB_DESIGN.clearSelectedDesign = function() {
    try {
      localStorage.removeItem(STORAGE_KEY);
      
      // Remove design inputs from all forms
      const designInputs = document.querySelectorAll('input[data-design-input="true"]');
      designInputs.forEach(function(input) {
        input.remove();
      });
      
      HATLAB_DESIGN.updateSelectedDesignBox();
      HATLAB_DESIGN.updateFileUploadVisibility();
      
      // Trigger custom event
      document.dispatchEvent(new CustomEvent('hatlabDesignCleared'));
      
      return true;
    } catch (e) {
      console.warn('Could not clear design:', e);
      return false;
    }
  };

  /**
   * Update the selected design box UI
   * Safely fails if box element doesn't exist
   */
  HATLAB_DESIGN.updateSelectedDesignBox = function() {
    const box = document.getElementById('SelectedDesignBox');
    if (!box) return; // Fail silently if element doesn't exist
    
    const design = HATLAB_DESIGN.getSelectedDesign();
    
    if (!design) {
      box.style.display = 'none';
      
      // Remove design inputs from all forms
      const designInputs = document.querySelectorAll('input[data-design-input="true"]');
      designInputs.forEach(function(input) {
        input.remove();
      });
      
      return;
    }
    
    // Update thumbnail
    const thumb = box.querySelector('.selected-design__thumb');
    if (thumb) {
      thumb.src = design.thumbnail;
      thumb.alt = design.title;
    }
    
    // Update name
    const name = box.querySelector('.selected-design__name');
    if (name) {
      name.textContent = design.title;
    }
    
    // Update tooltip image (use displayImage for UI, fallback to thumbnail)
    const tooltipImg = box.querySelector('.selected-design__tooltip img');
    if (tooltipImg) {
      tooltipImg.src = design.displayImage || design.thumbnail;
      tooltipImg.alt = design.title;
    }
    
    // Show the box
    box.style.display = '';
    
    // Add design to product form as hidden input
    HATLAB_DESIGN.addDesignToForm(design);
    
    // Update file upload visibility when design box updates
    HATLAB_DESIGN.updateFileUploadVisibility();
  };

  /**
   * Add design to product form as hidden input
   * @param {Object} design - Design object with title
   */
  HATLAB_DESIGN.addDesignToForm = function(design) {
    if (!design) return;
    
    // Find all product forms on the page
    const forms = document.querySelectorAll('form[action*="/cart/add"]');
    
    forms.forEach(function(form) {
      // Remove any existing design input
      const existingInput = form.querySelector('input[name="properties[Design]"]');
      if (existingInput) {
        existingInput.remove();
      }
      
      // Add new hidden input with design name
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[Design]';
      input.value = design.title;
      input.setAttribute('data-design-input', 'true');
      form.appendChild(input);
    });
  };

  /**
   * Toggle file upload visibility based on design selection
   * Hides uploads that have data-hide-when-design="true" attribute
   */
  HATLAB_DESIGN.updateFileUploadVisibility = function() {
    const hasDesign = !!HATLAB_DESIGN.getSelectedDesign();
    const uploads = document.querySelectorAll('[data-hide-when-design="true"]');
    
    uploads.forEach(function(upload) {
      if (hasDesign) {
        upload.style.display = 'none';
        upload.setAttribute('aria-hidden', 'true');
        
        // Disable inputs to prevent submission
        const inputs = upload.querySelectorAll('input, textarea, select');
        inputs.forEach(function(input) {
          input.disabled = true;
          if (input.required) {
            input.setAttribute('data-was-required', 'true');
            input.required = false;
          }
        });
      } else {
        upload.style.display = '';
        upload.setAttribute('aria-hidden', 'false');
        
        // Re-enable inputs
        const inputs = upload.querySelectorAll('input, textarea, select');
        inputs.forEach(function(input) {
          input.disabled = false;
          if (input.getAttribute('data-was-required') === 'true') {
            input.required = true;
            input.removeAttribute('data-was-required');
          }
        });
      }
    });
  };

  /**
   * Open design dialog with design data
   * @param {Object} designData - Design object to display
   * @param {Object} options - Optional configuration { context: 'gallery'|'product-page', inputId: string }
   */
  HATLAB_DESIGN.openLightbox = function(designData, options) {
    const dialogComponent = document.getElementById('DesignDialog');
    if (!dialogComponent) return;
    
    const dialog = dialogComponent.querySelector('dialog');
    if (!dialog) return;
    
    options = options || {};
    
    // Store context for later use (determines if we redirect or not)
    dialog.setAttribute('data-context', options.context || 'gallery');
    if (options.inputId) {
      dialog.setAttribute('data-input-id', options.inputId);
    }
    
    // Update dialog content (use displayImage for UI, fallback to image for backwards compatibility)
    const img = dialog.querySelector('.design-dialog__image');
    const title = dialog.querySelector('.design-dialog__title');
    const description = dialog.querySelector('.design-dialog__description');
    const selectBtn = dialog.querySelector('.design-dialog__select-btn');
    
    if (img) {
      img.src = designData.displayImage || designData.image;
      img.alt = designData.title;
    }
    if (title) title.textContent = designData.title;
    if (description) {
      description.textContent = designData.description || '';
      description.style.display = designData.description ? '' : 'none';
    }
    
    // Store design data on select button
    if (selectBtn) {
      selectBtn.setAttribute('data-design-data', JSON.stringify(designData));
      
      // Update button text based on context
      if (options.context === 'product-page') {
        selectBtn.textContent = 'Select This Design';
      } else {
        selectBtn.textContent = 'Select & Go to Hat Lab';
      }
    }
    
    // Use the theme's dialog component method to show
    if (dialogComponent.showDialog) {
      dialogComponent.showDialog();
    }
  };

  /**
   * Close design dialog
   */
  HATLAB_DESIGN.closeLightbox = function() {
    const dialogComponent = document.getElementById('DesignDialog');
    if (!dialogComponent) return;
    
    // Use the theme's dialog component method to close
    if (dialogComponent.closeDialog) {
      dialogComponent.closeDialog();
    }
  };

  /**
   * Trap focus within an element (for modals)
   * @param {HTMLElement} element - Element to trap focus within
   */
  HATLAB_DESIGN.trapFocus = function(element) {
    const focusableElements = element.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    if (focusableElements.length === 0) return;
    
    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];
    
    // Handle tab key
    function handleTab(e) {
      if (e.key !== 'Tab') return;
      
      if (e.shiftKey) {
        if (document.activeElement === firstFocusable) {
          lastFocusable.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === lastFocusable) {
          firstFocusable.focus();
          e.preventDefault();
        }
      }
    }
    
    element.addEventListener('keydown', handleTab);
  };

  /**
   * Get design picker destination setting
   * @returns {String} 'hat-lab', 'bulk-order', or 'both'
   */
  HATLAB_DESIGN.getDestination = function() {
    return window.DESIGN_PICKER_DESTINATION || 'hat-lab';
  };

  
  /**
   * Redirect to Hat Lab with design selected
   */
  HATLAB_DESIGN.redirectToHatlab = function() {
    // Get Hat Lab page URL from global variable set by Liquid
    const hatlabSlug = window.HATLAB_PAGE_SLUG || 'hat-lab';
    const hatlabUrl = '/pages/' + hatlabSlug + '?step=1';
    window.location.href = hatlabUrl;
  };

  /**
   * Redirect to Bulk Order with design selected
   */
  HATLAB_DESIGN.redirectToBulkOrder = function() {
    // Get Bulk Order page URL from global variable set by Liquid
    const bulkOrderSlug = window.BULK_ORDER_PAGE_SLUG || 'bulk-order';
    const bulkOrderUrl = '/pages/' + bulkOrderSlug + '?step=2';
    window.location.href = bulkOrderUrl;
  };

  /**
   * Handle redirect based on destination setting
   */
  HATLAB_DESIGN.handleDesignRedirect = function() {
    const destination = HATLAB_DESIGN.getDestination();
    
    if (destination === 'bulk-order') {
      HATLAB_DESIGN.redirectToBulkOrder();
    } else if (destination === 'both') {
      HATLAB_DESIGN.showDestinationModal();
    } else {
      // Default: hat-lab
      HATLAB_DESIGN.redirectToHatlab();
    }
  };

  /**
   * Show destination choice modal
   */
  HATLAB_DESIGN.showDestinationModal = function() {
    // Check if modal already exists
    let modal = document.getElementById('DesignDestinationModal');
    
    if (!modal) {
      // Create modal
      const hatlabLabel = window.DESIGN_PICKER_HATLAB_LABEL || 'Bring to Hat Lab';
      const bulkOrderLabel = window.DESIGN_PICKER_BULK_ORDER_LABEL || 'Bring to Bulk Order';
      
      modal = document.createElement('div');
      modal.id = 'DesignDestinationModal';
      modal.className = 'design-destination-modal';
      modal.setAttribute('role', 'dialog');
      modal.setAttribute('aria-modal', 'true');
      modal.setAttribute('aria-labelledby', 'DesignDestinationTitle');
      modal.innerHTML = `
        <div class="design-destination-modal__backdrop"></div>
        <div class="design-destination-modal__content">
          <h3 id="DesignDestinationTitle" class="design-destination-modal__title">Where would you like to use this design?</h3>
          <p class="design-destination-modal__subtitle">Choose your destination to continue with your selected design.</p>
          <div class="design-destination-modal__actions">
            <button type="button" class="design-destination-modal__btn design-destination-modal__btn--hatlab" data-destination="hat-lab">
              ${hatlabLabel}
            </button>
            <button type="button" class="design-destination-modal__btn design-destination-modal__btn--bulk-order" data-destination="bulk-order">
              ${bulkOrderLabel}
            </button>
          </div>
          <button type="button" class="design-destination-modal__close" aria-label="Close">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
      `;
      
      document.body.appendChild(modal);
      
      // Add modal styles if not already present
      if (!document.getElementById('DesignDestinationModalStyles')) {
        const styles = document.createElement('style');
        styles.id = 'DesignDestinationModalStyles';
        styles.textContent = `
          .design-destination-modal {
            position: fixed;
            inset: 0;
            z-index: 9999;
            display: none;
            align-items: center;
            justify-content: center;
            padding: 20px;
          }
          .design-destination-modal.is-open {
            display: flex;
          }
          .design-destination-modal__backdrop {
            position: absolute;
            inset: 0;
            background: rgba(0, 0, 0, 0.5);
          }
          .design-destination-modal__content {
            position: relative;
            background: #fff;
            border-radius: 12px;
            padding: 2rem;
            max-width: 400px;
            width: 100%;
            text-align: center;
            box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
          }
          .design-destination-modal__title {
            font-size: 1.25rem;
            font-weight: 700;
            margin: 0 0 0.5rem;
          }
          .design-destination-modal__subtitle {
            font-size: 0.875rem;
            color: #6b7280;
            margin: 0 0 1.5rem;
          }
          .design-destination-modal__actions {
            display: flex;
            flex-direction: column;
            gap: 0.75rem;
          }
          .design-destination-modal__btn {
            padding: 0.875rem 1.5rem;
            font-size: 1rem;
            font-weight: 600;
            border-radius: 8px;
            cursor: pointer;
            transition: all 0.2s ease;
            font-family: inherit;
          }
          .design-destination-modal__btn--hatlab {
            background: #000;
            color: #fff;
            border: 2px solid #000;
          }
          .design-destination-modal__btn--hatlab:hover {
            background: #fff;
            color: #000;
          }
          .design-destination-modal__btn--bulk-order {
            background: transparent;
            color: #000;
            border: 2px solid #000;
          }
          .design-destination-modal__btn--bulk-order:hover {
            background: #000;
            color: #fff;
          }
          .design-destination-modal__close {
            position: absolute;
            top: 12px;
            right: 12px;
            background: none;
            border: none;
            padding: 4px;
            cursor: pointer;
            color: #6b7280;
          }
          .design-destination-modal__close:hover {
            color: #000;
          }
        `;
        document.head.appendChild(styles);
      }
      
      // Bind events
      modal.querySelector('.design-destination-modal__backdrop').addEventListener('click', function() {
        HATLAB_DESIGN.hideDestinationModal();
      });
      
      modal.querySelector('.design-destination-modal__close').addEventListener('click', function() {
        HATLAB_DESIGN.hideDestinationModal();
      });
      
      modal.querySelectorAll('.design-destination-modal__btn').forEach(function(btn) {
        btn.addEventListener('click', function() {
          const dest = this.getAttribute('data-destination');
          HATLAB_DESIGN.hideDestinationModal();
          if (dest === 'bulk-order') {
            HATLAB_DESIGN.redirectToBulkOrder();
          } else {
            HATLAB_DESIGN.redirectToHatlab();
          }
        });
      });
      
      // Handle escape key
      document.addEventListener('keydown', function(e) {
        if (e.key === 'Escape' && modal.classList.contains('is-open')) {
          HATLAB_DESIGN.hideDestinationModal();
        }
      });
    }
    
    // Show modal
    modal.classList.add('is-open');
    modal.querySelector('.design-destination-modal__btn--hatlab').focus();
  };

  /**
   * Hide destination choice modal
   */
  HATLAB_DESIGN.hideDestinationModal = function() {
    const modal = document.getElementById('DesignDestinationModal');
    if (modal) {
      modal.classList.remove('is-open');
    }
  };

  /**
   * Initialize design gallery interactions
   */
  HATLAB_DESIGN.initGallery = function() {
    // Design card hover interactions
    const designCards = document.querySelectorAll('.design-card');
    designCards.forEach(function(card) {
      const hatlabBtn = card.querySelector('.design-card__hatlab-btn');
      
      if (hatlabBtn) {
        hatlabBtn.addEventListener('click', function(e) {
          e.preventDefault();
          e.stopPropagation();
          
          const designData = JSON.parse(card.getAttribute('data-design'));
          designData.selectedFrom = 'gallery-hover';
          
          if (HATLAB_DESIGN.selectDesign(designData)) {
            HATLAB_DESIGN.handleDesignRedirect();
          }
        });
      }
      
      // Click card to open lightbox
      card.addEventListener('click', function() {
        const designData = JSON.parse(this.getAttribute('data-design'));
        HATLAB_DESIGN.openLightbox(designData);
      });
    });
    
    // Dialog handlers
    const dialogComponent = document.getElementById('DesignDialog');
    if (dialogComponent) {
      const dialog = dialogComponent.querySelector('dialog');
      const selectBtn = dialog ? dialog.querySelector('.design-dialog__select-btn') : null;
      
      if (selectBtn) {
        selectBtn.addEventListener('click', function() {
          const designData = JSON.parse(this.getAttribute('data-design-data'));
          const context = dialog.getAttribute('data-context') || 'gallery';
          const inputId = dialog.getAttribute('data-input-id');
          
          designData.selectedFrom = context === 'product-page' ? 'product-block' : 'gallery-lightbox';
          
          if (HATLAB_DESIGN.selectDesign(designData)) {
            HATLAB_DESIGN.closeLightbox();
            
            // If on product page with selector, check the input instead of redirecting
            if (context === 'product-page' && inputId) {
              const input = document.getElementById(inputId);
              if (input) {
                input.checked = true;
                // Trigger change event to update hidden inputs for checkbox mode
                input.dispatchEvent(new Event('change', { bubbles: true }));
              }
            } else {
              // From gallery - redirect based on destination setting
              HATLAB_DESIGN.handleDesignRedirect();
            }
          }
        });
      }
    }
    
    // Remove design button handler
    const removeBtn = document.querySelector('.selected-design__remove');
    if (removeBtn) {
      removeBtn.addEventListener('click', function() {
        HATLAB_DESIGN.clearSelectedDesign();
      });
    }
    
    // Click thumbnail to open lightbox
    const thumb = document.querySelector('.selected-design__thumb');
    if (thumb) {
      thumb.addEventListener('click', function() {
        const design = HATLAB_DESIGN.getSelectedDesign();
        if (design) {
          HATLAB_DESIGN.openLightbox(design);
        }
      });
    }
  };

  /**
   * Initialize on page load
   */
  function init() {
    // Update selected design box if present
    if (typeof HATLAB_DESIGN.updateSelectedDesignBox === 'function') {
      HATLAB_DESIGN.updateSelectedDesignBox();
    }
    
    // Initialize gallery interactions if present
    HATLAB_DESIGN.initGallery();
  }

  // Expose backwards compatible global functions
  window.updateSelectedDesignBox = HATLAB_DESIGN.updateSelectedDesignBox;
  window.updateFileUploadVisibility = HATLAB_DESIGN.updateFileUploadVisibility;

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-initialize on Shopify section load
  document.addEventListener('shopify:section:load', init);
})();

